import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import AppLayout from './components/layout/AppLayout';
import LoginPage from './pages/LoginPage';
import { registerPushServiceWorker, syncPushSubscription } from './services/pushNotifications';

// Lazy-load heavy pages to split the bundle
const DashboardPage      = lazy(() => import('./pages/DashboardPage'));
const ProjectsPage       = lazy(() => import('./pages/ProjectsPage'));
const ProjectProfilePage = lazy(() => import('./pages/ProjectProfilePage'));
const TasksPage          = lazy(() => import('./pages/TasksPage'));
const SprintsPage        = lazy(() => import('./pages/SprintsPage'));
const ReportsPage        = lazy(() => import('./pages/ReportsPage'));
const CRMPage            = lazy(() => import('./pages/CRMPage'));
const NotificationsPage  = lazy(() => import('./pages/NotificationsPage'));
const GanttPage          = lazy(() => import('./pages/GanttPage'));
const ProfilePage        = lazy(() => import('./pages/ProfilePage'));
const SettingsPage       = lazy(() => import('./pages/SettingsPage'));
const ClientPortalPage   = lazy(() => import('./pages/ClientPortalPage'));
const PortalManagementPage = lazy(() => import('./pages/PortalManagementPage'));
const PortfolioPage      = lazy(() => import('./pages/PortfolioPage'));
const ApprovalsPage      = lazy(() => import('./pages/ApprovalsPage'));
const CalendarPage       = lazy(() => import('./pages/CalendarPage'));
const TemplatesPage      = lazy(() => import('./pages/TemplatesPage'));
const BitacoraPage       = lazy(() => import('./pages/BitacoraPage'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh] bg-slate-950">
      <div className="w-7 h-7 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin" />
    </div>
  );
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" replace />;
}

function normalizeRole(role: string | undefined): string {
  const r = (role ?? '').toLowerCase();
  return r.includes('admin') ? 'admin' : r;
}

function RoleRoute({ children, roles }: { children: React.ReactNode; roles: string[] }) {
  const { isAuthenticated, user } = useAuthStore();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!roles.includes(normalizeRole(user?.role))) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  return !isAuthenticated ? <>{children}</> : <Navigate to="/dashboard" replace />;
}

export default function App() {
  const { verifySession, isAuthenticated } = useAuthStore();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    registerPushServiceWorker().catch(() => undefined);
    localStorage.removeItem('accessToken');
    localStorage.removeItem('auth-storage');
    verifySession().finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    syncPushSubscription().catch(() => undefined);
  }, [isAuthenticated]);

  if (checking) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          {/* Portal de cliente — ruta pública sin autenticación */}
          <Route path="/portal/:token" element={<ClientPortalPage />} />
          <Route element={<PrivateRoute><AppLayout /></PrivateRoute>}>
            <Route path="/dashboard"          element={<DashboardPage />} />
            <Route path="/projects"           element={<ProjectsPage />} />
            <Route path="/projects/:id"       element={<ProjectProfilePage />} />
            <Route path="/tasks"              element={<TasksPage />} />
            <Route path="/sprints"            element={<SprintsPage />} />
            <Route path="/calendar"           element={<CalendarPage />} />
            <Route path="/templates"          element={<TemplatesPage />} />
            <Route path="/bitacora"           element={<BitacoraPage />} />
            <Route path="/reports"            element={<PrivateRoute><ReportsPage /></PrivateRoute>} />
            <Route path="/crm"                element={<RoleRoute roles={['admin', 'project_manager']}><CRMPage /></RoleRoute>} />
            <Route path="/notifications"      element={<NotificationsPage />} />
            <Route path="/gantt"              element={<GanttPage />} />
            <Route path="/profile"            element={<ProfilePage />} />
            <Route path="/settings"           element={<PrivateRoute><SettingsPage /></PrivateRoute>} />
            <Route path="/portal-management"  element={<PortalManagementPage />} />
            <Route path="/portfolio"          element={<PortfolioPage />} />
            <Route path="/approvals"          element={<ApprovalsPage />} />
            <Route path="/"                   element={<Navigate to="/dashboard" replace />} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
